import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Integration from "@/model/interface/Integration";
import Membership from "@/model/interface/Membership";
import { useGetMembersQuery, useSetPodioUserMappingMutation } from "@/store/rest_api";
import toast from "react-hot-toast";

interface Props {
  integration: Integration;
}

function UserMappingRow({
  member,
  onSave,
}: {
  member: Membership;
  onSave: (membershipId: string, podioUserId: string | null) => Promise<void>;
}) {
  const initialValue = (member as any).podioUserId || "";
  const [value, setValue] = useState<string>(initialValue);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setValue(initialValue);
    setDirty(false);
  }, [initialValue]);

  const handleSave = async () => {
    await onSave(member.id, value.trim() || null);
    setDirty(false);
  };

  return (
    <div className="grid grid-cols-[1fr_1fr_auto] gap-4 items-center">
      <div>
        <p className="text-sm font-medium">
          {member.user?.firstName} {member.user?.lastName}
        </p>
        <p className="text-xs text-muted-foreground">{member.user?.email}</p>
      </div>
      <Input
        placeholder="Podio user ID"
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
          setDirty(true);
        }}
      />
      <Button size="sm" disabled={!dirty} onClick={handleSave}>
        Save
      </Button>
    </div>
  );
}

export default function PodioUserMapping({ integration }: Props) {
  const isActive = integration.status?.toLowerCase() === "active";
  const { data: members } = useGetMembersQuery({ status: "ACTIVE" });
  const [setMapping] = useSetPodioUserMappingMutation();

  const handleSave = async (membershipId: string, podioUserId: string | null) => {
    try {
      await setMapping({ membershipId, podioUserId }).unwrap();
      toast.success("User mapping updated");
    } catch {
      toast.error("Failed to update user mapping");
    }
  };

  if (!isActive) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Connect your Podio account first to configure user mapping.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>User Mapping</CardTitle>
        <CardDescription>
          Link team members to their Podio user IDs. You can find the Podio user ID in the profile URL of each user.
          Calls and messages will be attributed to the mapped Podio user.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-[1fr_1fr_auto] gap-4 text-xs text-muted-foreground font-medium">
            <span>Team Member</span>
            <span>Podio User ID</span>
            <span className="w-14" />
          </div>

          {members?.map((member) => (
            <UserMappingRow key={member.id} member={member} onSave={handleSave} />
          ))}

          {(!members || members.length === 0) && (
            <p className="text-sm text-muted-foreground text-center py-4">No team members found</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
